import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { lastValueFrom } from 'rxjs';
import { PartnerService } from '../api/partner.service';
import { RegisterService } from '../api/register.service';
import { getBrowserLanguageCode, isValidLanguage } from '../utils/language.utils';

/**
 * Route guard that handles language-based navigation and redirection in the application.
 * This guard ensures that all routes have a valid language parameter and handles language
 * selection based on authentication status, registration flow, password reset, and partner preferences.
 *
 * Behavior Flow:
 * 1. New User Registration (Azure AD):
 *    - If user is new, handles registration process with different types:
 *      - EMAIL: Redirects to auth page with email registration success
 *      - LETTER: Redirects to auth page with letter registration success
 *      - SWISS_ID: Redirects to auth page with Swiss ID registration success
 *
 * 2. Password Reset:
 *    - If user completed password reset (acr = 'b2c_1a_password_reset'):
 *      Redirects to auth page with password reset success message
 *
 * 3. Azure AD Authentication:
 *    - If authenticated via Azure AD: Redirects to '/conf'
 *
 * 4. For unauthenticated users (Azure B2C):
 *    - If URL has valid language: Uses that language and proceeds
 *    - If no/invalid language: Redirects to /auth with detected browser language
 *
 * 5. For authenticated users (Azure B2C):
 *    - If partner exists and has different valid language:
 *      Redirects to partner's preferred language
 *    - Otherwise: Allows navigation with current language
 *
 * @param route - The activated route snapshot containing URL parameters
 * @returns {Promise<boolean>}
 *    - Resolves to true if navigation should proceed
 *    - Resolves to false if guard initiates a redirect
 *
 * @example
 * // Route: /fr/home with valid authentication
 * // Partner language matches route language
 * languageRedirectGuard() // returns true
 *
 * // Route: /fr/home with valid authentication
 * // Partner language is 'de'
 * languageRedirectGuard() // redirects to /de/home and returns false
 *
 * // Route: /invalid/home
 * // Invalid language code
 * languageRedirectGuard() // redirects to /{browserLang}/auth and returns false
 */

export async function languageRedirectGuard(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
  const router = inject(Router);
  const translateService = inject(TranslateService);
  const oidcSecurityService = inject(OidcSecurityService);
  const partnerService = inject(PartnerService);
  const registerService = inject(RegisterService);

  const azureAdAuth = await lastValueFrom(oidcSecurityService.checkAuth(undefined, 'azureAd'));
  const azureB2cAuth = await lastValueFrom(oidcSecurityService.checkAuth(undefined, 'azureB2c'));

  const defaultLang = getBrowserLanguageCode();

  // check: after registration
  if (sessionStorage.getItem('registrationState') === 'inprogress') {
    // reset register success, otherwise the user will be redirected to the auth page again and again and again... and they lived happily ever after...
    sessionStorage.setItem('registrationState', 'completed');
    await lastValueFrom(oidcSecurityService.logoff());
    return router.navigate([`/${defaultLang}/auth`]);
  }

  // handle new user registration - only if registrationState has not been set yet
  if (azureB2cAuth.userData?.newUser && !sessionStorage.getItem('registrationState')) {
    try {
      sessionStorage.setItem('registrationState', 'inprogress');
      const registerResponse = await lastValueFrom(registerService.register(azureB2cAuth.idToken));

      if (registerResponse.type === 'EMAIL') {
        sessionStorage.setItem('registerType', 'email');
      } else if (registerResponse.type === 'LETTER') {
        sessionStorage.setItem('registerType', 'letter');
      } else if (registerResponse.type === 'SWISS_ID') {
        sessionStorage.setItem('registerType', 'swissId');
      }

      oidcSecurityService.logoffLocalMultiple();
      return router.navigate([`/${defaultLang}/auth`]);
    } catch (error) {
      console.error('Error registering user', error as Error);
      await lastValueFrom(oidcSecurityService.logoff());

      return router.navigate([`/${defaultLang}/auth`]);
    }
  }

  // handle password reset
  if (azureB2cAuth.userData?.acr === 'b2c_1a_password_reset') {
    return router.navigate([`/${defaultLang}/auth`], { queryParams: { passwordReseSuccess: 'true' } });
  }

  // go to admin page when this is a AD user
  if (azureAdAuth.isAuthenticated) {
    return router.navigate(['/conf']);
  }

  const routeLang = route.paramMap.get('lang');

  // if not logged in as B2C user => go to /{lang}/auth
  if (!azureB2cAuth.isAuthenticated) {
    if (isValidLanguage(routeLang)) {
      await lastValueFrom(translateService.use(routeLang));
      return true;
    }
    return router.navigate([`/${defaultLang}/auth`]);
  }

  // if the user is already on the auth page, don't do anything just navigate to the page
  // no need for getting the current partner
  if (state.url.includes('/auth')) {
    return true;
  }

  // if user is authenticated, try to load their partnerdata from be: /me
  if (azureB2cAuth.isAuthenticated) {
    try {
      // if the partner is not found, the user will be redirected to the auth page but no error will be thrown
      const partner = await lastValueFrom(partnerService.getLoggedInPartner());
      if (partner) {
        const partnerLang = partner.portalSprache.toLowerCase();

        if (partnerLang !== routeLang && isValidLanguage(partnerLang)) {
          await lastValueFrom(translateService.use(partnerLang));
          return router.navigate([`/${partnerLang}/home`]);
        }
      }
    } catch (error) {
      // partner loading failed - this only happens when we are told that we are logged in before
      // this handles cases like REGISTRATION_NOT_FINISHED (token says user is new and we are logged in)
      console.error('Error getting partner', (error as Error).message);

      // log out locally and on server, clean registerType
      // this should allow to register a new user
      await lastValueFrom(oidcSecurityService.logoff());
      sessionStorage.removeItem('registerType');
      return router.navigate([`/${defaultLang}/auth`]);
    }
  }

  return true;
}
